import { InputHTMLAttributes, ReactNode, forwardRef } from "react";
import "./FloatingField.scss";

interface FloatingFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Visible field label that floats into the outline on focus/fill. */
  label: string;
  /** Required — links the <label> to the <input> and preserves existing ids. */
  id: string;
  /** Optional trailing affordance rendered inside the field (e.g. a password toggle). */
  trailing?: ReactNode;
  /** Marks the field invalid (red label + border). */
  hasError?: boolean;
}

/**
 * MD3 outlined text field with a floating label.
 *
 * The label floats when the input is focused OR contains a value. This relies on
 * `placeholder=" "` so the `:placeholder-shown` selector can detect an empty
 * field — do not override the placeholder. Spread react-hook-form's
 * `register(...)` directly onto this component; the ref is forwarded to <input>.
 */
const FloatingField = forwardRef<HTMLInputElement, FloatingFieldProps>(
  ({ label, id, trailing, hasError, className, ...rest }, ref) => (
    <div
      className={`md-field${trailing ? " md-field--trailing" : ""}${
        hasError ? " md-field--error" : ""
      }`}
    >
      <input
        ref={ref}
        id={id}
        placeholder=" "
        className={`form-control${className ? ` ${className}` : ""}`}
        {...rest}
      />
      <label htmlFor={id}>{label}</label>
      {trailing}
    </div>
  )
);

FloatingField.displayName = "FloatingField";

export default FloatingField;
